import { useContext } from "react";
import { LoadingContext, ShownError, showErrorMessage } from "../components/appPane";
import { LoginContext } from "../context/loginContext";
import { LAMBDA_URL, MESSAGES, X_API_KEY } from "../constants/const";

export const REQ_AUTH_USER: string = `${LAMBDA_URL}auth`;
export const REQ_LOGOUT: string = `${LAMBDA_URL}logout`;

interface APIRequestOptions {
  method: string; // 'GET', 'POST', 'PUT', 'DELETE', etc.
  headers: HeadersInit; // 任意のリクエストヘッダー
  body?: BodyInit; // 任意のリクエストボディ
}

let requestCount = 0;
export type JsonResponse = { [key: string]: any };
export type TextResponse = string;
export type BinaryResponse = Blob;
type APIResponseType = TextResponse | JsonResponse;

interface APIResponse<T extends APIResponseType> {
  status: number;
  payload: T;
}

export interface API {
  getRequest: <T extends APIResponseType>(url: string) => Promise<APIResponse<T>>;
  postRequestJson: <T extends APIResponseType>(
    url: string,
    bodyObj?: any
  ) => Promise<APIResponse<T>>;
  putRequestJson: <T extends APIResponseType>(url: string, bodyObj: any) => Promise<APIResponse<T>>;
  deleteRequest: <T extends APIResponseType>(url: string) => Promise<APIResponse<T>>;
  postRequestBinary: <T extends APIResponseType>(
    url: string,
    binaryBody: Uint8Array
  ) => Promise<APIResponse<T>>;
  abortController: AbortController;
}

export function useApi(window?: Window) {
  const { setLoading } = useContext(LoadingContext);
  const { setIsLogin } = useContext(LoginContext);
  const pendingRequests: { [key: number]: boolean } = {};

  const abortController = new AbortController();

  //非公開
  async function apiRequest<T extends APIResponseType>(
    url: string,
    options: APIRequestOptions
  ): Promise<APIResponse<T>> {
    const requestId = ++requestCount % 10000;
    pendingRequests[requestId] = true;

    setLoading(true);
    const promise = fetch(url, {
      method: options.method,
      headers: options.headers,
      body: options.body,
      credentials: "include", // クッキー送信 フロントエンドがlocalhost:3000以外のクロスオリジンの場合はAPIGatewayのCORS設定から許可を追加してください
      signal: abortController.signal,
    }).then(
      async response => {
        const contentType = response.headers.get("content-type") || "";
        let status = response.status;
        let message; // text/plain
        let jsonObj; // application/json
        let binary; // other

        if (/text\/plain/.test(contentType)) {
          message = await response.text();
        } else if (/application\/json/.test(contentType)) {
          jsonObj = await response.json();
          status = jsonObj.statusCode || response.status;
        } else {
          // バイナリ
          binary = await response.blob();
        }

        const apiResponse = { status, payload: message || jsonObj || binary };

        console.log(`api response`, apiResponse);

        return apiResponse;
      },
      e => {
        // AbortErrorは共通処理でハンドリング
        if (e.name === "AbortError") {
          return { status: 401, payload: "" as any };
        }
        throw e;
      }
    );

    const dismissLoading = new Promise<void>(resolve => {
      setTimeout(() => {
        promise.finally(() => {
          delete pendingRequests[requestId];
          setLoading(false);
          resolve();
        });
      });
    });

    const { status, payload } = await promise;

    // ユーザ認証APIとログアウトAPIは共通処理内の401エラー処理を無視 呼び出し元で独自処理を行う
    const apiEndpoint = url.split("/").pop() || "";
    const handleError = !/^(auth|logout)$/.test(apiEndpoint);

    if (status === 401 || status === 403) {
      if (handleError) {
        // 他のAPIリクエストも中止
        abortController.abort();

        // サーバの認証エラー時にはエラーメッセージが含まれるためダイアログで表示 クライアントのAbortによるエラーではメッセージを持たせないため無視
        if (payload.errorMessage) {
          showErrorMessage(MESSAGES.ERROR_SESSION_TIMEOUT, { window });
          setIsLogin(false);
        }
      }
    } else if (status !== 200) {
      const errorMessage =
        (typeof payload === "string" ? payload : payload.errorMessage) || MESSAGES.ERROR_SERVER;

      // ローディング非表示後にエラーメッセージ表示
      dismissLoading.then(() => {
        showErrorMessage(errorMessage, { window });
      });

      // 成功ダイアログメッセージ表示待機中の可能性があるので例外をスローしてキャンセル
      throw new ShownError(errorMessage);
    }

    return Promise.all([promise, dismissLoading]).then(([response]) => response);
  }

  function defineHeaders(): Headers {
    const retHeaders: Headers = new Headers();
    retHeaders.append("Accept", "application/json");
    return retHeaders;
  }

  //公開
  const getRequest = async <T extends APIResponseType>(url: string): Promise<APIResponse<T>> => {
    const pushHeaders: Headers = defineHeaders();
    pushHeaders.append("Content-Type", "application/json");
    pushHeaders.append("x-api-key", X_API_KEY);
    const option: APIRequestOptions = {
      method: "GET",
      headers: pushHeaders,
    };
    return apiRequest(url, option);
  };

  const postRequestJson = async <T extends APIResponseType>(
    url: string,
    bodyObj?: any
  ): Promise<APIResponse<T>> => {
    const pushHeaders: Headers = defineHeaders();
    pushHeaders.append("Content-Type", "application/json");
    pushHeaders.append("x-api-key", X_API_KEY);
    const option: APIRequestOptions = {
      method: "POST",
      headers: pushHeaders,
    };
    if (bodyObj) option.body = JSON.stringify(bodyObj);
    return apiRequest(url, option);
  };

  const putRequestJson = async <T extends APIResponseType>(
    url: string,
    bodyObj: any
  ): Promise<APIResponse<T>> => {
    const pushHeaders: Headers = defineHeaders();
    pushHeaders.append("Content-Type", "application/json");
    pushHeaders.append("x-api-key", X_API_KEY);
    const option: APIRequestOptions = {
      method: "PUT",
      headers: pushHeaders,
      body: JSON.stringify(bodyObj),
    };
    return apiRequest(url, option);
  };

  const deleteRequest = async <T extends APIResponseType>(url: string): Promise<APIResponse<T>> => {
    const pushHeaders: Headers = defineHeaders();
    pushHeaders.append("Content-Type", "application/json");
    pushHeaders.append("x-api-key", X_API_KEY);
    const option: APIRequestOptions = {
      method: "DELETE",
      headers: pushHeaders,
    };
    return apiRequest(url, option);
  };

  const postRequestBinary = async <T extends APIResponseType>(
    url: string,
    binaryBody: Uint8Array<ArrayBuffer>
  ): Promise<APIResponse<T>> => {
    const pushHeaders: Headers = defineHeaders();
    pushHeaders.append("x-api-key", X_API_KEY);
    const option: APIRequestOptions = {
      method: "POST",
      headers: pushHeaders,
      body: binaryBody,
    };

    return apiRequest(url, option);
  };

  return {
    getRequest,
    postRequestJson,
    putRequestJson,
    deleteRequest,
    postRequestBinary,
    abortController,
  } as API;
}
